<template>
  <div v-if="hasContent" class="selectFromParentType">
    <slot />
    <VText
      v-if="typeName === 'subgenre' && !pValidity"
      cfg="sans/14/medium"
      class="tw-relative -tw-top-4 tw-text-error"
    >
      {{ $t('tags.selectFromTypeLoopOnParent.subgenre.noSelectionError') }}
    </VText>
    <div class="gridContent">
      <div
        v-for="configuration in CONFIGURATIONS"
        :key="configuration.parentTag.id"
        class="parentWrap"
      >
        <VText
          cfg="sans/16/medium"
          class="tw-mb-4 tw-block tw-rounded-xs tw-bg-gray-100 tw-px-6 tw-py-4"
        >
          {{
            $t(
              `parentTags.${CHILD_TAG_TYPE.name}.${configuration.parentTag.name}`,
            )
          }}
        </VText>
        <TagGridTemplate>
          <TagElement
            v-for="childTag in configuration.childTags"
            :key="childTag.id"
            :selected="tagIdIsSelected(childTag.id)"
            :tag="childTag"
            :i18nkey="typeName"
            :error="!pValidity"
            @click="handleTagElementClick(childTag.id)"
          />
        </TagGridTemplate>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TagElement from '~/components/tag/element.vue'
import TagGridTemplate from '~/components/tag/gridTemplate.vue'
import VText from '~/components/ui/VText.vue'

import { useSortTagsByTranslatedName } from '~/composables/useTagHelpers'

import { useTagStore } from '~/stores/tag'

import type Tag from '~/entities/tag'
import type TagParent from '~/entities/tagParent'
import type TagType from '~/entities/tagType'
import type { TagParentTypeKey } from '~/types/tagParentTypeKey'

export interface Configuration {
  parentTag: TagParent
  childTags: Tag[]
}

type Props = {
  maxAmount?: number
  disallowed?: number[]
  includeUnderscoreTags?: boolean
  typeName: TagParentTypeKey
  modelValue: number[]
  pValidity?: boolean
}

type Emits = {
  'update:modelValue': [payload: number[]]
  change: []
}

const props = withDefaults(defineProps<Props>(), {
  maxAmount: 0,
  disallowed: () => [],
  includeUnderscoreTags: false,
  pValidity: true,
})

const emit = defineEmits<Emits>()

const sortTagsByTranslatedName = useSortTagsByTranslatedName()

const {
  GET_TAGS_FROM_PARENT_ID,
  GET_PARENT_BY_IDS: GET_TAG_PARENT_BY_IDS,
  GET_TAG_TYPE_FROM_NAME,
} = useTagStore()

/**
 * Since this module loops on Parents the configured TagType can only be the ones that have a corresponding ParentTag.
 *
 * @description Hence the enforced TagParentTypeKey type on the typeName value.
 *
 * @returns {TagType | undefined} If the fetch is successful it returns the corresponding TagType object.
 */
const CHILD_TAG_TYPE = computed<TagType | { name: ''; parent_ids: [] }>(
  () =>
    GET_TAG_TYPE_FROM_NAME(props.typeName) ?? {
      name: '',
      parent_ids: [],
    },
)

const AVAILABLE_PARENTS = computed<TagParent[]>(() =>
  GET_TAG_PARENT_BY_IDS(CHILD_TAG_TYPE.value?.parent_ids || [])
    .filter((parentTag) => {
      return props.includeUnderscoreTags || !parentTag.name.includes('root')
    })
    .sort((a, b) => sortTagsByTranslatedName(props.typeName, a, b, true)),
)

const CONFIGURATIONS = computed<Configuration[]>(() =>
  AVAILABLE_PARENTS.value.reduce<Configuration[]>((accumulator, parentTag) => {
    const configuration: Configuration = {
      parentTag,
      childTags: filterOutDisallowed(parentTag.id).sort((a, b) =>
        sortTagsByTranslatedName(props.typeName, a, b),
      ),
    }

    if (configuration.childTags.length) accumulator.push(configuration)

    return accumulator
  }, []),
)

const hasContent = computed<boolean>(() =>
  CONFIGURATIONS.value.some(
    (configuration) => configuration.childTags.length > 0,
  ),
)

function filterOutDisallowed(parentId: number): Tag[] {
  return GET_TAGS_FROM_PARENT_ID(parentId).filter((tag) => {
    return !props.disallowed.includes(tag.id)
  })
}

function tagIdIsSelected(tagId: number): boolean {
  return props.modelValue.includes(tagId)
}

function handleTagElementClick(tagId: number): void {
  const copyOfSelectedTagIds = [...props.modelValue]
  const index = copyOfSelectedTagIds.indexOf(tagId)

  if (
    index === -1 &&
    (!props.maxAmount || props.modelValue.length < props.maxAmount)
  )
    copyOfSelectedTagIds.push(tagId)
  else if (index !== -1) copyOfSelectedTagIds.splice(index, 1)

  emit('update:modelValue', copyOfSelectedTagIds)
  emit('change')
}
</script>

<style scoped lang="scss">
.selectFromParentType > .gridContent {
  @apply tw-grid tw-grid-cols-1 tw-gap-4;

  @screen 375 {
    @apply tw-gap-6;
  }

  @screen md {
    @apply tw-gap-10;
  }
}
</style>
